import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import client, { extractError } from "@/api";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const strengthOk = password.length >= 8;
  const matchOk = password === confirm && password.length > 0;

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!token) {
      setError("This password reset link is missing its token. Please request a new one.");
      return;
    }
    if (!strengthOk) {
      setError("Please choose a password of at least 8 characters.");
      return;
    }
    if (!matchOk) {
      setError("Passwords do not match. Please retype your new password.");
      return;
    }
    setBusy(true);
    try {
      await client.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(extractError(err, "We could not reset your password. Your link may be invalid or expired."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <Seo
        title="Reset Password · Barter India"
        description="Choose a new password for your Barter India account."
        path="/reset-password"
        robots="noindex,follow"
      />
      <section className="auth-intro">
        <div className="brand-lockup" data-testid="brand-lockup"><span className="brand-mark">B</span><span>Barter India</span></div>
        <div className="intro-copy">
          <p className="eyebrow">ACCOUNT RECOVERY</p>
          <h1>Choose a<br /><em>new password</em><br />for your account.</h1>
          <p>Pick a password of at least 8 characters. You will be signed in again after resetting.</p>
        </div>
        <div className="intro-footer"><span>Barter India Support</span><span className="status-dot">● TLS · bcrypt hashed</span></div>
      </section>
      <section className="auth-panel">
        <div className="auth-form-wrap">
          {done ? (
            <div data-testid="reset-success">
              <h2><CheckCircle2 size={20} /> Password updated</h2>
              <p className="muted">Your password has been reset. Redirecting you to sign in…</p>
              <p><Link to="/login" data-testid="reset-go-login">Go to sign in now</Link></p>
            </div>
          ) : (
            <>
              <h2>Reset your password</h2>
              <p className="muted">Please choose a strong password you have not used before.</p>
              <form onSubmit={submit} data-testid="reset-password-form">
                <label htmlFor="password">New password</label>
                <input id="password" data-testid="reset-password-input"
                       type="password" value={password}
                       onChange={(e) => setPassword(e.target.value)}
                       placeholder="Minimum 8 characters" required minLength={8}
                       autoComplete="new-password" />
                <label htmlFor="confirm">Confirm new password</label>
                <input id="confirm" data-testid="reset-confirm-input"
                       type="password" value={confirm}
                       onChange={(e) => setConfirm(e.target.value)}
                       placeholder="Retype your new password" required minLength={8}
                       autoComplete="new-password" />
                {!token && (
                  <div className="form-error" data-testid="reset-missing-token">
                    This reset link is missing its token. Please request a new one.
                  </div>
                )}
                {error && <div className="form-error" data-testid="reset-error">{error}</div>}
                <button className="primary-button" type="submit"
                        data-testid="reset-submit-button"
                        disabled={busy || !token || !strengthOk || !matchOk}>
                  {busy ? "Resetting…" : "Reset Password"} <ArrowUpRight size={17} />
                </button>
              </form>
              <p className="form-note">
                <Link to="/forgot-password" data-testid="reset-resend-link">Need a new link?</Link>
                {"  ·  "}
                <Link to="/login" data-testid="reset-back-login">Back to sign in</Link>
              </p>
              <p className="form-note" data-testid="security-note">
                <ShieldCheck size={15} /> Your password is stored only as a secure bcrypt hash
              </p>
            </>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
